import { useState, type JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreateCroRequestSchema,
  CreateSponsorRequestSchema,
  type CroDto,
  type SponsorDto,
} from '@ctms/contracts';
import { AlertCircle, X } from 'lucide-react';

import { getApiErrorMessage } from '../../api/client.js';
import { trialsApi } from './trials.api.js';
import { trialQueryKeys } from './trials.query-keys.js';

export interface SponsorCroPanelsProps {
  sponsors: SponsorDto[];
  cros: CroDto[];
  isTenantAdmin: boolean;
}

interface AddNameDialogProps {
  title: string;
  nameLabel: string;
  saveLabel: string;
  emptyMessage: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
}

export function AddNameDialog({
  title,
  nameLabel,
  saveLabel,
  emptyMessage,
  isOpen,
  onClose,
  onSave,
}: AddNameDialogProps): JSX.Element | null {
  const [name, setName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) {
    return null;
  }

  const handleClose = (): void => {
    setName('');
    setErrorMessage(null);
    onClose();
  };

  const handleSave = async (): Promise<void> => {
    const parsedName = name.trim();
    if (parsedName.length === 0) {
      setErrorMessage(emptyMessage);
      return;
    }
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await onSave(parsedName);
      setName('');
      onClose();
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się zapisać pozycji słownika.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-dictionary-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100">
        <div className="flex items-center justify-between">
          <h2 id="add-dictionary-title" className="text-base font-semibold">
            {title}
          </h2>
          <button type="button" aria-label="Zamknij" onClick={handleClose} className="text-slate-400">
            <X className="h-4 w-4" />
          </button>
        </div>
        {errorMessage && (
          <div role="alert" className="mt-4 flex items-center gap-2 text-xs text-rose-300">
            <AlertCircle className="h-4 w-4" />
            <span>{errorMessage}</span>
          </div>
        )}
        <label htmlFor="dictionary-name" className="mt-4 block text-xs font-medium uppercase tracking-wider text-slate-300">
          {nameLabel}
        </label>
        <input
          id="dictionary-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={handleClose} className="rounded-md border border-slate-700 px-3 py-1.5 text-xs">
            Anuluj
          </button>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => void handleSave()}
            className="rounded-md bg-cyan-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function NameTable({ rows }: { rows: Array<{ id: number; name: string }> }): JSX.Element {
  return (
    <table className="w-full text-left text-xs">
      <thead>
        <tr className="border-b border-slate-800 text-slate-400">
          <th scope="col" className="py-2">Nazwa</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} className="border-b border-slate-800/80">
            <td className="py-2 text-slate-100">{row.name}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function SponsorCroPanels({
  sponsors,
  cros,
  isTenantAdmin,
}: SponsorCroPanelsProps): JSX.Element {
  const queryClient = useQueryClient();
  const [sponsorOpen, setSponsorOpen] = useState(false);
  const [croOpen, setCroOpen] = useState(false);

  const createSponsor = useMutation({
    mutationFn: (name: string) => {
      const body = CreateSponsorRequestSchema.parse({ name });
      return trialsApi.createSponsor(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trialQueryKeys.sponsors.all });
    },
  });

  const createCro = useMutation({
    mutationFn: (name: string) => {
      const body = CreateCroRequestSchema.parse({ name });
      return trialsApi.createCro(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trialQueryKeys.cros.all });
    },
  });

  return (
    <div className="grid grid-cols-2 gap-4">
      <section aria-label="Słownik sponsorów" className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
        <h3 className="text-sm font-semibold text-slate-100">Słownik sponsorów</h3>
        <NameTable rows={sponsors} />
        {isTenantAdmin && (
          <button
            type="button"
            onClick={() => setSponsorOpen(true)}
            className="mt-3 rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
          >
            Dodaj sponsora
          </button>
        )}
      </section>

      <section aria-label="Słownik CRO" className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
        <h3 className="text-sm font-semibold text-slate-100">Słownik CRO</h3>
        <NameTable rows={cros} />
        {isTenantAdmin && (
          <button
            type="button"
            onClick={() => setCroOpen(true)}
            className="mt-3 rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
          >
            Dodaj CRO
          </button>
        )}
      </section>

      <AddNameDialog
        title="Dodaj sponsora"
        nameLabel="Nazwa sponsora"
        saveLabel="Zapisz sponsora"
        emptyMessage="Nazwa sponsora jest wymagana"
        isOpen={sponsorOpen}
        onClose={() => setSponsorOpen(false)}
        onSave={async (name) => {
          await createSponsor.mutateAsync(name);
        }}
      />
      <AddNameDialog
        title="Dodaj organizację CRO"
        nameLabel="Nazwa CRO"
        saveLabel="Zapisz CRO"
        emptyMessage="Nazwa CRO jest wymagana"
        isOpen={croOpen}
        onClose={() => setCroOpen(false)}
        onSave={async (name) => {
          await createCro.mutateAsync(name);
        }}
      />
    </div>
  );
}
